import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, Input, Label, Textarea } from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import { createSkill, updateSkill } from "@/lib/api/endpoints";
import { qk } from "@/lib/query/keys";
import type { SkillView } from "@/types/api";

function parseJsonOrThrow(label: string, text: string): Record<string, unknown> | null {
  const t = text.trim();
  if (!t) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(t);
  } catch {
    throw new Error(`${label} must be valid JSON.`);
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error(`${label} must be a JSON object.`);
  }
  return parsed as Record<string, unknown>;
}

/** Create (skill undefined) or edit a skill on a version. */
export function SkillDialog({
  open,
  onOpenChange,
  agentSlug,
  label,
  skill,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentSlug: string;
  label: string;
  skill?: SkillView;
}) {
  const qc = useQueryClient();
  const editing = !!skill;
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [instructions, setInstructions] = React.useState("");
  const [inputSchema, setInputSchema] = React.useState("");
  const [outputSchema, setOutputSchema] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setName(skill?.name ?? "");
      setDescription(skill?.description ?? "");
      setInstructions(skill?.instructions ?? "");
      setInputSchema(skill?.input_schema ? JSON.stringify(skill.input_schema, null, 2) : "");
      setOutputSchema(skill?.output_schema ? JSON.stringify(skill.output_schema, null, 2) : "");
    }
  }, [open, skill]);

  const mutation = useMutation({
    mutationFn: () => {
      const input_schema = parseJsonOrThrow("Input schema", inputSchema);
      const output_schema = parseJsonOrThrow("Output schema", outputSchema);
      const body = {
        name,
        description: description || null,
        instructions: instructions || null,
        input_schema,
        output_schema,
      };
      return editing
        ? updateSkill(agentSlug, label, skill!.slug, body)
        : createSkill(agentSlug, label, body);
    },
    onSuccess: async () => {
      toast.success(editing ? "Skill updated." : "Skill added.");
      await qc.invalidateQueries({ queryKey: qk.skills(agentSlug, label) });
      await qc.invalidateQueries({ queryKey: qk.version(agentSlug, label) });
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit skill “${skill?.name}”` : "Add skill"}</DialogTitle>
          <DialogDescription>
            A skill is a structured invocation. Schemas are optional JSON Schema objects.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) mutation.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="s-name">Name</Label>
            <Input
              id="s-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Summarise"
              autoFocus
              disabled={editing}
            />
            {editing ? (
              <p className="text-xs text-muted-foreground">
                Slug <code className="font-mono">{skill?.slug}</code> is fixed once created.
              </p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-desc">Description</Label>
            <Textarea
              id="s-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="When should the model use this skill? e.g. Questions about staff, including Julio."
            />
            <p className="text-xs text-muted-foreground">
              The model reads this to decide whether to call the skill in chat. Without it, a
              truncated preview of the instructions is used instead.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-instr">Instructions</Label>
            <Textarea
              id="s-instr"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={3}
              placeholder="Skill-specific prompt augmentation (optional)"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="s-in">Input schema (JSON)</Label>
              <Textarea
                id="s-in"
                value={inputSchema}
                onChange={(e) => setInputSchema(e.target.value)}
                rows={6}
                className="font-mono text-xs"
                placeholder='{ "type": "object" }'
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-out">Output schema (JSON)</Label>
              <Textarea
                id="s-out"
                value={outputSchema}
                onChange={(e) => setOutputSchema(e.target.value)}
                rows={6}
                className="font-mono text-xs"
                placeholder='{ "type": "object" }'
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || mutation.isPending}>
              {mutation.isPending ? "Saving…" : editing ? "Save" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
